/**
 * Search params the upcoming-events booking dialog owns.
 *
 * Dropped when building the dialog's close link (so the page's filters
 * survive), and used as the ignore list for the page's `shouldRevalidate` (so
 * opening the dialog, or anything the visitor does inside it, doesn't re-run
 * the page's 250-event loader).
 *
 * `from` IS the dialog's — it anchors which day the dialog lists, and the
 * page's own start date is `startDate`. The two were the same param until the
 * dialog turned out to need the event's date while the list needed the
 * visitor's: sharing it meant a Book click re-anchored and reloaded the list
 * behind the dialog, and the dialog couldn't open on the right day at all.
 */
export const DIALOG_OWNED_PARAMS = [
  'experienceId',
  'eventId',
  'from',
  't',
  'language',
  'locationId',
] as const;

/** True for the dialog's own URL (`…/upcoming-events/book`, or its
 *  single-fetch `.data` form). */
export function isDialogPath(pathname: string): boolean {
  return /\/book(\.data)?$/.test(pathname);
}

/** The page's path, with the dialog's `/book` segment (and any single-fetch
 *  `.data` suffix) removed — so the page and its open dialog compare equal. */
export function stripDialogSuffix(pathname: string): string {
  return pathname.replace(/\.data$/, '').replace(/\/book$/, '');
}

/**
 * Where the dialog's close button goes: the page it opened from, keeping the
 * filters the visitor set. Without dropping only the dialog's own params,
 * closing would either carry dialog state into the page URL or reset the
 * visitor's list to the unfiltered default.
 */
export function closeToPath(url: URL): string {
  const params = new URLSearchParams(url.search);
  for (const key of DIALOG_OWNED_PARAMS) params.delete(key);

  // `…/upcoming-events/book` → `…/upcoming-events`. A single-fetch request's
  // pathname can also end in `.data`; strip that first so the close link is a
  // document URL (see US-11497).
  const pathname = url.pathname
    .replace(/\.data$/, '')
    .replace(/\/book$/, '');

  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
}
